import { useDispatch } from 'react-redux'
import { APPLICATION_STRINGS } from '../../strings'
import PeripheralBridge from '../../utils/peripheralBridge'
import { setWebSDKDataReceive } from '../../VNAndroidSDK/ActionCreators'
import VNDataBridgeReceiveType from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataReceive'
import { setUserProgress } from '../ActionCreators'
import { USER_PROGRESS } from '../Enums'
import { CFD_SCREEN } from '../Reducer'
import { setSpinner } from '../../VNLoader/ActionCreators'
import createDataDogLog from '../../utils/datadog'
import { ToastManager } from '../../utils/toastManager'
import { useCallback } from 'react'

// this is a just-in-case delay because we should be relying on
// isCfdWebViewLoaded to confirm A3700 is loaded and operational
const RECONNECT_WAIT_THRESHOLD = 5_000
const RECONNECT_RETRIES = 5
export const RECONNECT_MIN_APK_VERSION = '2.20.0'

const REPLAY_AUTH_TIMEOUT = 10_000
const rejectError = new Error(APPLICATION_STRINGS.ERROR_FETCH_CARD_TRANSACTION)

/**
 * While returning from BroadPOS using an A3700 as a CFD, it's possible for the
 * POS app to lose connectivity
 */
const useAuthStatus = () => {
  const dispatch = useDispatch()

  const replayResultHandler = useCallback(
    (data, invoiceNumber) => {
      const errorData = {
        ...USER_PROGRESS.CARD_PAYMENT.ERROR,
        error: true,
        subTitle: `${data.reasonText}\n Error Code: ${data.reasonCode}`,
        flow: CFD_SCREEN.TRANSACTION_ERROR,
      }
      if (data?.reasonText === 'NOT FOUND' || data.message === 'NOT FOUND') {
        dispatch(
          setUserProgress({
            ...errorData,
            subTitle: 'Card Payment Error.\nPlease try again.',
          }),
        )
        return { error: 'Authorization Not Found' }
      }

      if (invoiceNumber && data?.invoiceNumber !== invoiceNumber) {
        throw rejectError
      }
      if (data.reasonText === 'DECLINE' || data.message === 'DECLINE') {
        dispatch(setUserProgress(errorData))
        return { error: 'Authorization Failed' }
      }
      if (data?.status === 'OK' || data?.reasonText === 'OK') {
        dispatch(
          setWebSDKDataReceive({
            method: VNDataBridgeReceiveType.VN_TRANSACTION_RESULT,
            data,
          }),
        )
        return { success: 'Authorization Approved' }
      }
      if (data?.reasonText === 'APPROVED OFFLINE' || data?.isOfflineApproval) {
        dispatch(
          setWebSDKDataReceive({
            method: VNDataBridgeReceiveType.VN_TRANSACTION_RESULT,
            data,
          }),
        )
        return { success: 'Authorization Approved Offline' }
      }
    },
    [dispatch],
  )

  const replayAuth = useCallback((invoiceNumber) => {
    return new Promise((resolve, reject) => {
      if (!invoiceNumber?.length) {
        return reject(rejectError)
      }
      const bridgeInstance = PeripheralBridge.getBridge()
      bridgeInstance.registerHandler('replayAuthStatus', resolve)
      bridgeInstance.callHandler('checkStatus', { requestId: invoiceNumber })

      setTimeout(() => {
        const timeoutError = new Error('Replay Auth Timeout')
        timeoutError.timeout = true
        reject(timeoutError)
      }, REPLAY_AUTH_TIMEOUT)
    })
  }, [])

  /**
   * Fetch card data result by invoiceNumber
   *
   * @param {String} invoiceNumber
   * @returns {Promise<Object>} Resolves with the result of the card data fetch
   */
  const getCardDataResult = useCallback(
    async (invoiceNumber) => {
      if (!invoiceNumber?.length) {
        return { error: rejectError }
      }
      try {
        const result = await replayAuth(invoiceNumber)
        return replayResultHandler(result, invoiceNumber)
      } catch (error) {
        throw error
      }
    },
    [replayAuth, replayResultHandler],
  )

  const attemptReconnect = useCallback(
    ({ curInvoiceNumber, reconnectTimerRef, reconnectRetriesRef, isReconnectedRef }) => {
      if (!curInvoiceNumber) return
      if (reconnectRetriesRef?.current >= RECONNECT_RETRIES) return

      dispatch(setSpinner(true, APPLICATION_STRINGS.RECONNECTED_FETCHING_DATA))

      // fallback on a timed retry to fetch card transaction
      return setTimeout(async () => {
        isReconnectedRef.current = false
        try {
          const result = await getCardDataResult(curInvoiceNumber)
          clearTimeout(reconnectTimerRef?.current)
          reconnectTimerRef.current = null
          if (!result.error) {
            dispatch(setSpinner(false))
            createDataDogLog(
              'debug',
              `[CFD RECONNECT] Success fetching card data INVOICE NUMBER - ${curInvoiceNumber} - ${result.success || ''}`,
            )
            return { success: result.success }
          }
          ToastManager.error(result.error)
        } catch (e) {
          clearTimeout(reconnectTimerRef?.current)
          reconnectTimerRef.current = null
          if (e.timeout && reconnectRetriesRef?.current < RECONNECT_RETRIES) {
            isReconnectedRef.current = true
            reconnectRetriesRef.current += 1
          }

          if (reconnectRetriesRef?.current >= RECONNECT_RETRIES) {
            dispatch(
              setUserProgress({
                ...USER_PROGRESS.CARD_PAYMENT.ERROR,
                error: true,
                subTitle: 'Card Payment Error.\nPlease try again.',
                flow: CFD_SCREEN.TRANSACTION_ERROR,
              }),
            )
            isReconnectedRef.current = false
            reconnectRetriesRef.current = 0
            // we are not handling - Replay Auth Timeout
            createDataDogLog('error', `[CFD RECONNECT] ${e.message || 'Error requesting card info'} - Maximum retries reached`)
          }
        }
        dispatch(setSpinner(false))
      }, RECONNECT_WAIT_THRESHOLD)
    },
    [dispatch, getCardDataResult],
  )

  return { attemptReconnect, getCardDataResult }
}

export default useAuthStatus
